import {HEAP_USERPROPERTY_STRING_LIMIT, TRUNCATED_HEAP_USERPROPERTY_DOT_COUNT} from '../const';
import {getEnv} from './envUtil';

/**
 * Generates the heap.addUserProperties script which will take in the JSON string, parse it, and send it to Heap. We
 * do it this way so that we can see exactly what we are sending to Heap on our page.
 * @param {object} userProperties
 * @returns {string}
 */
export const generateAddUserPropertiesScript = (userProperties) => {
  return `heap.addUserProperties(JSON.parse(\`${JSON.stringify(userProperties)}\`));`;
};

/**
 * This generates all of the userProperties for Heap. It joins the userDetails, subscription data, and feature flags
 * to build a JSON object which can be added to the heap.addUserProperties() function
 * and no values are over 1024 chars in length,
 * @param {Object} userProperties
 * @return {Object} sanitizedUserProperties
 */
export const sanitizeHeapUserProperties = ({userDetails, featureFlags, hasSubscriptions, hasFieldviewSubscription}) => {
  let sanitizedUserProperties = {};
  if (typeof userDetails !== 'undefined') {
    const { uuid, id, countryCode } = userDetails;
    if (uuid && id && countryCode) {
      sanitizedUserProperties = {
        climate_account_id: id,
        climate_account_country: countryCode,
        build_environment: getEnv()
      };
      sanitizedUserProperties.climate_account_id = id;
      sanitizedUserProperties.climate_account_country = countryCode;
      sanitizedUserProperties.build_environment = getEnv();
    }
  }
  if (hasSubscriptions) {
    sanitizedUserProperties.has_fieldview_subscription = `${hasFieldviewSubscription}`;
  }
  if (typeof featureFlags !== 'undefined') {
    Object.entries(featureFlags).forEach(([key, value]) => {
      sanitizedUserProperties[key] = sanitizeValueForHeapUserProperty(value);
    });
  }
  return sanitizedUserProperties;
};


/**
 * We may want to sanitize things like launch darkly flags, etc. But we must ensure that the values meet Heap guidelines:
 * https://developers.heap.io/reference/add-user-properties
 * Properties must be a number or a string with <1024 chars. We will also truncate the end of the string and add dots
 * to indicate truncation
 * @param value
 * @returns {string}
 */
export const sanitizeValueForHeapUserProperty = (value) => {
  if (typeof value === 'number') {
    return value;
  }
  let retVal = '';
  const charLimit = HEAP_USERPROPERTY_STRING_LIMIT;
  const dotCount = TRUNCATED_HEAP_USERPROPERTY_DOT_COUNT;
  // @TODO Ensure there are UTs to strip out control chars
  // @TODO We want to change "/` to '
  // @TODO We want to change []{}() to | chars
  if (typeof value === 'string') {
    retVal = sanitizeString(value);
  } else {
    retVal = JSON.stringify(value).replaceAll('"', '\'');
  }
  if (retVal.length > charLimit) {
    // This adds N dots at the end of the string to indicate that it was truncated
    retVal = retVal.slice(0, charLimit - 1 - dotCount) + Array.from({length: dotCount}).fill('.').join('');
  }
  return retVal;
};

/**
 * This sanitizes a string to ensure non ascii and control chars are removed, and that brackets, etc. are replaced with |
 * and quote chars won't break JSON parsing
 * @param {string} string
 */
export const sanitizeString = (string) => {
  let retVal;
  // Strip out non ASCII chars
  const sanitizedChars = [];
  string.split('').forEach(c => {
    const charCode = c.charCodeAt(0);
    if (charCode >= 32 && charCode <= 127) {
      sanitizedChars.push(c);
    }
  });
  retVal = sanitizedChars.join('');
  const replaceChars = '"\'`[](){}';
  const replaceWith = '\'';
  replaceChars.split('').forEach(c => {
    retVal = retVal.replace(c, replaceWith);
  });
  return retVal;
};
